import React, { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { MessageSquare, X, Send, Paperclip, FileText, Mic } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

export default function ChatDrawer() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [unread, setUnread] = useState(0);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [recording, setRecording] = useState(false);
  const [recSeconds, setRecSeconds] = useState(0);
  const scrollRef = useRef(null);
  const fileRef = useRef(null);
  const recorderRef = useRef(null);
  const recTimerRef = useRef(null);
  const recStartRef = useRef(0);

  const loadUnread = useCallback(() => {
    api.get("/chat/unread-count")
      .then((r) => setUnread(r.data.unread || 0))
      .catch((err) => console.error("chat unread poll failed", err));
  }, []);

  const loadThread = useCallback(() => {
    api.get("/chat/my").then((r) => {
      setMessages(r.data.messages || []);
      setUnread(0);
    }).catch((err) => console.error("chat thread load failed", err));
  }, []);

  useEffect(() => {
    if (!user || user.role === "admin") return;
    loadUnread();
    const int = setInterval(loadUnread, 15000);
    return () => clearInterval(int);
  }, [user, loadUnread]);

  useEffect(() => {
    if (open) {
      loadThread();
      const int = setInterval(loadThread, 8000);
      return () => clearInterval(int);
    }
  }, [open, loadThread]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  const sendMessage = async (payload) => {
    try {
      const { data } = await api.post("/chat/send", payload);
      setMessages((m) => [...m, data]);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send");
    }
  };

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() || busy) return;
    setBusy(true);
    try {
      await sendMessage({ text: text.trim() });
      setText("");
    } finally { setBusy(false); }
  };

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/chat/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
      await sendMessage({
        text: text.trim(),
        attachment_url: data.url,
        attachment_type: data.type,
        attachment_name: data.name,
      });
      setText("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const uploadBlob = async (blob, filename, duration) => {
    const form = new FormData();
    form.append("file", blob, filename);
    const { data } = await api.post("/chat/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
    await sendMessage({
      text: text.trim(),
      attachment_url: data.url,
      attachment_type: data.type,
      attachment_name: data.name,
      attachment_duration: duration,
    });
    setText("");
  };

  const startRecording = async () => {
    if (!navigator.mediaDevices?.getUserMedia) { toast.error("Voice recording not supported"); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "";
      const rec = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      const chunks = [];
      rec.ondataavailable = (e) => { if (e.data.size > 0) chunks.push(e.data); };
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        clearInterval(recTimerRef.current);
        const duration = (Date.now() - recStartRef.current) / 1000;
        setRecording(false);
        setRecSeconds(0);
        const blob = new Blob(chunks, { type: rec.mimeType || "audio/webm" });
        if (blob.size === 0) return;
        try {
          setUploading(true);
          await uploadBlob(blob, `voice-${Date.now()}.webm`, duration);
        } catch (err) {
          toast.error(err.response?.data?.detail || "Voice send failed");
        } finally {
          setUploading(false);
        }
      };
      recorderRef.current = rec;
      recStartRef.current = Date.now();
      rec.start();
      setRecording(true);
      setRecSeconds(0);
      recTimerRef.current = setInterval(() => {
        const s = Math.floor((Date.now() - recStartRef.current) / 1000);
        setRecSeconds(s);
        if (s >= 180) { stopRecording(); }
      }, 500);
    } catch (err) {
      toast.error("Microphone permission denied");
    }
  };

  const stopRecording = () => {
    try { recorderRef.current?.stop(); } catch (err) { console.error("stopRecording failed", err); }
  };

  const cancelRecording = () => {
    try {
      const rec = recorderRef.current;
      if (rec) {
        rec.ondataavailable = null;
        rec.onstop = () => { rec.stream.getTracks().forEach((t) => t.stop()); };
        rec.stop();
      }
    } catch (err) { console.error("cancelRecording failed", err); }
    clearInterval(recTimerRef.current);
    setRecording(false);
    setRecSeconds(0);
  };

  const attachmentSrc = (url) => {
    const token = localStorage.getItem("sse_token");
    const base = process.env.REACT_APP_BACKEND_URL || "";
    return `${base}${url}${token ? `?auth=${encodeURIComponent(token)}` : ""}`;
  };

  if (!user || user.role === "admin") return null;

  return (
    <>
      <button
        onClick={() => setOpen((o) => !o)}
        className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-[#00FF66] text-black shadow-[0_10px_40px_rgba(0,255,102,0.4)] hover:scale-105 transition-transform flex items-center justify-center"
        data-testid="chat-fab"
        aria-label="Chat with admin"
      >
        {open ? <X className="w-6 h-6" /> : <MessageSquare className="w-6 h-6" />}
        {unread > 0 && !open && (
          <span className="absolute -top-1 -right-1 min-w-[22px] h-[22px] rounded-full bg-red-500 text-white text-[11px] font-black flex items-center justify-center px-1 border-2 border-[#0A0A0A]" data-testid="chat-unread">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="fixed bottom-24 right-6 z-50 w-[92vw] max-w-sm h-[70vh] max-h-[560px] bg-[#111] border border-white/10 rounded-2xl shadow-2xl flex flex-col overflow-hidden" data-testid="chat-drawer">
          <div className="p-4 border-b border-white/10 bg-black/40">
            <div className="text-xs uppercase tracking-[0.3em] text-[#00FF66] font-bold">Chat with Admin</div>
            <div className="text-[11px] text-white/50 mt-0.5">Reply time · under 5 minutes on business hours</div>
          </div>
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-2.5">
            {messages.length === 0 ? (
              <div className="text-center text-white/40 text-sm mt-8" data-testid="chat-empty">
                Start the conversation. Ask about a lead, a payout, or contest tips.
              </div>
            ) : messages.map((m) => (
              <div key={m.id} className={`flex ${m.from_role === "admin" ? "justify-start" : "justify-end"}`} data-testid={`chat-msg-${m.id}`}>
                <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${
                  m.from_role === "admin"
                    ? "bg-white/5 text-white border border-white/10 rounded-bl-sm"
                    : "bg-[#00FF66] text-black rounded-br-sm"
                }`}>
                  {m.attachment_url && m.attachment_type === "image" && (
                    <a href={attachmentSrc(m.attachment_url)} target="_blank" rel="noreferrer" data-testid={`chat-img-${m.id}`}>
                      <img
                        src={attachmentSrc(m.attachment_url)}
                        alt={m.attachment_name || "attachment"}
                        className="rounded-lg max-w-[220px] max-h-[220px] object-cover mb-1.5 border border-black/10"
                      />
                    </a>
                  )}
                  {m.attachment_url && m.attachment_type === "audio" && (
                    <audio
                      src={attachmentSrc(m.attachment_url)}
                      controls
                      preload="metadata"
                      className="mb-1.5 max-w-[220px]"
                      data-testid={`chat-audio-${m.id}`}
                    />
                  )}
                  {m.attachment_url && m.attachment_type === "file" && (
                    <a
                      href={attachmentSrc(m.attachment_url)}
                      target="_blank"
                      rel="noreferrer"
                      className={`flex items-center gap-2 mb-1.5 px-2 py-1.5 rounded-lg text-xs font-medium ${m.from_role === "admin" ? "bg-white/10 text-white hover:bg-white/15" : "bg-black/10 text-black hover:bg-black/15"}`}
                      data-testid={`chat-file-${m.id}`}
                    >
                      <FileText className="w-4 h-4 flex-shrink-0" />
                      <span className="truncate max-w-[180px]">{m.attachment_name || "Attachment"}</span>
                    </a>
                  )}
                  {m.text && <div className="leading-snug whitespace-pre-wrap break-words">{m.text}</div>}
                  <div className={`text-[10px] mt-1 opacity-60 ${m.from_role === "admin" ? "text-white/50" : "text-black/60"}`}>
                    {new Date(m.created_at).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <form onSubmit={send} className="p-3 border-t border-white/10 bg-black/40 flex items-center gap-2">
            <input
              type="file"
              ref={fileRef}
              onChange={(e) => upload(e.target.files?.[0])}
              accept="image/png,image/jpeg,image/webp,application/pdf"
              className="hidden"
              data-testid="chat-file-input"
            />
            {recording ? (
              <div className="flex-1 flex items-center gap-2 bg-red-500/10 border border-red-500/30 rounded-full px-3 h-10" data-testid="chat-recording-bar">
                <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                <span className="text-xs text-white font-mono flex-1">
                  Recording · {String(Math.floor(recSeconds / 60)).padStart(2, "0")}:{String(recSeconds % 60).padStart(2, "0")}
                </span>
                <button type="button" onClick={cancelRecording} className="text-white/60 hover:text-white text-xs" data-testid="chat-recording-cancel">Cancel</button>
                <button
                  type="button"
                  onClick={stopRecording}
                  className="w-8 h-8 rounded-full bg-[#00FF66] text-black flex items-center justify-center hover:bg-[#00E55C]"
                  data-testid="chat-recording-stop"
                  aria-label="Stop and send"
                >
                  <Send className="w-3.5 h-3.5" />
                </button>
              </div>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  disabled={uploading}
                  className="w-10 h-10 rounded-full border border-white/15 bg-black/40 hover:border-[#00FF66]/60 flex items-center justify-center text-white/70 hover:text-[#00FF66] transition-colors disabled:opacity-50 flex-shrink-0"
                  data-testid="chat-attach"
                  aria-label="Attach file"
                >
                  {uploading ? <span className="text-[10px] font-mono">…</span> : <Paperclip className="w-4 h-4" />}
                </button>
                <button
                  type="button"
                  onClick={startRecording}
                  disabled={uploading}
                  className="w-10 h-10 rounded-full border border-white/15 bg-black/40 hover:border-[#00FF66]/60 flex items-center justify-center text-white/70 hover:text-[#00FF66] transition-colors disabled:opacity-50 flex-shrink-0"
                  data-testid="chat-mic"
                  aria-label="Record voice note"
                >
                  <Mic className="w-4 h-4" />
                </button>
                <input
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder="Type a message…"
                  className="flex-1 bg-black/40 border border-white/10 rounded-full px-4 h-10 text-sm text-white outline-none focus:border-[#00FF66]/60"
                  data-testid="chat-input"
                />
                <Button type="submit" disabled={busy || !text.trim()} className="rounded-full bg-[#00FF66] text-black hover:bg-[#00E55C] font-semibold h-10 w-10 p-0 flex-shrink-0" data-testid="chat-send">
                  <Send className="w-4 h-4" />
                </Button>
              </>
            )}
          </form>
        </div>
      )}
    </>
  );
}
